import { useEffect, useState } from 'react'
import { api } from '../lib/api.js'
import { agentMeta, agentLabel, ago, badgeTitle, landingWords, tally } from '../lib/agentStamp.js'
import styles from './AgentBadge.module.css'

/* ========================================================================= *
 * THE JARVIS / DOT BADGE (Brian 2026-10-26). Presentation only: the agent
 * comes stamped from the server and is never inferred here.
 *
 * Pure views (AgentBadge, LandingLine, LandingList, EngineAgentList) take
 * data as props so they render the same on the server in tests. The fetching
 * wrappers (LastLanding, RecentLandings, EngineAgents) own one read each and
 * say so when it fails, never rendering a failed read as "nobody landed".
 * ========================================================================= */

export function AgentBadge({ agent, actor = null, at = null, compact = false, now }) {
  const m = agentMeta(agent)
  return (
    <span className={`${styles.badge} ${styles[m.tone]}`} title={badgeTitle({ agent, actor, at }, now)}
      aria-label={badgeTitle({ agent, actor, at }, now)} data-agent={m.key}>
      <span className={styles.mark} aria-hidden="true">{m.name.charAt(0)}</span>
      <span>{m.name}</span>
      {!compact && m.maker && <span className={styles.maker}>{'·'} {m.maker}</span>}
    </span>
  )
}

export function LandingLine({ landing, now, showRecord = false }) {
  if (!landing) return null
  return (
    <span className={styles.line}>
      <AgentBadge agent={landing.agent} actor={landing.actor} at={landing.at} now={now} />
      <span>{landingWords(landing.field)}{showRecord && landing.recordName ? ` · ${landing.recordName}` : ''}</span>
      {landing.at && <span className={styles.when}>{ago(landing.at, now)}</span>}
      {landing.agentStored === false && <span className={styles.when}>(before stamping; derived)</span>}
    </span>
  )
}

export function LandingList({ landings, now, empty = 'Nothing has landed on these records yet.', nameOf = null }) {
  if (!Array.isArray(landings)) return null
  if (!landings.length) return <p className={styles.note}>{empty}</p>
  const t = tally(landings)
  return (
    <div>
      <div className={styles.tally} aria-label="Landings by agent">
        {['jarvis', 'dot', 'human', 'unstamped'].filter((k) => t[k]).map((k) => (
          <span key={k} className={styles.line}><AgentBadge agent={k} compact now={now} /> {t[k]}</span>
        ))}
      </div>
      <ul className={styles.list}>
        {landings.map((l, i) => (
          <li key={`${l.recordId}-${l.at}-${i}`}>
            {/* Tapping a landing opens the record it landed on. */}
            <a className={styles.item} href={`/command-center?record=${encodeURIComponent(String(l.recordId))}`}>
              <LandingLine landing={{ ...l, recordName: nameOf ? nameOf(l.recordId) : null }} now={now} showRecord={!!nameOf} />
            </a>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** The newest landing on ONE record (championship drawer header). */
export function LastLanding({ recordId }) {
  const [state, setState] = useState({ status: 'loading', landing: null, error: null, scope: null })
  useEffect(() => {
    if (!recordId) return undefined
    const ctl = new AbortController()
    setState({status: 'loading', landing: null, error: null, scope: recordId})
    api.landings({ recordId, limit: 1, signal: ctl.signal })
      .then((r) => { if (!ctl.signal.aborted) setState({ status: 'ok', landing: r?.landings?.[0] || null, error: null, scope: recordId }) })
      .catch((e) => { if (!ctl.signal.aborted) setState({ status: 'error', landing: null, error: String(e?.message || e), scope: recordId }) })
    return () => ctl.abort()
  }, [recordId])
  if (!recordId) return null
  if (state.scope !== recordId || state.status === 'loading') return <span className={styles.when}>Reading who landed the last change...</span>
  if (state.status === 'error') return <span className={styles.error}>Could not read who landed the last change: {state.error}</span>
  if (!state.landing) return <span className={styles.when}>No change recorded on this record yet.</span>
  return <LandingLine landing={state.landing} />
}

/** Recent landings: across the estate (no ids) or for a set of records (max 25). */
export function RecentLandings({ recordIds = null, limit = 20, nameOf = null, empty }) {
  const key = Array.isArray(recordIds) ? recordIds.join(',') : ''
  const scope = `${Array.isArray(recordIds) ? 'records:' + key : 'estate'}:${limit}`
  const [state, setState] = useState({ status: 'loading', landings: null, error: null, capped: null, scope: null })
  useEffect(() => {
    if (Array.isArray(recordIds) && !recordIds.length) { setState({ status: 'ok', landings: [], error: null, capped: null, scope }); return undefined }
    const ctl = new AbortController()
    setState({status: 'loading', landings: null, error: null, capped: null, scope})
    const params = key ? { recordIds: key, signal: ctl.signal } : { limit, signal: ctl.signal }
    api.landings(params)
      .then((r) => {
        if (ctl.signal.aborted) return
        const rows = [...(r?.landings || [])].sort((a, b) => String(b.at).localeCompare(String(a.at)))
        setState({ status: 'ok', landings: rows, error: null, capped: r?.capped || null, scope })
      })
      .catch((e) => { if (!ctl.signal.aborted) setState({ status: 'error', landings: null, error: String(e?.message || e), capped: null, scope }) })
    return () => ctl.abort()
  }, [scope]) // eslint-disable-line react-hooks/exhaustive-deps
  if (state.scope !== scope || state.status === 'loading') return <p className={styles.note}>Reading who landed what...</p>
  if (state.status === 'error') return <p className={styles.error}>Could not read landings: {state.error}. This is not an empty list.</p>
  return (
    <div>
      <LandingList landings={state.landings} nameOf={nameOf} empty={empty} />
      {state.capped && <p className={styles.note}>{state.capped}</p>}
    </div>
  )
}

/** Engines and who declared their last run (deploy/health panel). */
export function EngineAgentList({ engines, now }) {
  if (!Array.isArray(engines)) return null
  const rows = engines.filter((e) => e.state !== 'archived')
  if (!rows.length) return <p className={styles.note}>No engine has reported yet.</p>
  return (
    <ul className={styles.list}>
      {rows.map((e) => (
        /* A readout, not a control: an engine row has nothing to open here. */
        <li key={e.engine} className={styles.item} aria-disabled="true">
          <AgentBadge agent={e.agent} actor={e.agentActor} at={e.lastRunAt} now={now} />
          <b>{e.engine}</b>
          <span>{e.health || e.state}</span>
          <span className={styles.when}>{e.lastRunAt ? `last run ${ago(e.lastRunAt, now)}` : 'never declared a run'}</span>
        </li>
      ))}
    </ul>
  )
}

export function EngineAgents() {
  const [state, setState] = useState({ status: 'loading', engines: null, error: null, generatedAt: null })
  useEffect(() => {
    const ctl = new AbortController()
    api.engineHeartbeat({ signal: ctl.signal })
      .then((r) => { if (!ctl.signal.aborted) setState({ status: 'ok', engines: r?.engines || [], error: null, generatedAt: r?.generatedAt || null }) })
      .catch((e) => { if (!ctl.signal.aborted) setState({ status: 'error', engines: null, error: String(e?.message || e), generatedAt: null }) })
    return () => ctl.abort()
  }, [])
  if (state.status === 'loading') return <p className={styles.note}>Reading engine health...</p>
  if (state.status === 'error') return <p className={styles.error}>Could not read engine health: {state.error}. This is not an all-clear.</p>
  return (
    <div>
      <EngineAgentList engines={state.engines} />
      <p className={styles.note}>
        Server-stamped from the identity that posted each run. Deploys are stamped on their BUILD LOG receipt;
        an engine that reads Unstamped has not been attributed to {agentLabel('jarvis')} or {agentLabel('dot')} yet.
        {state.generatedAt ? ` Read ${ago(state.generatedAt)}.` : ''}
      </p>
    </div>
  )
}

export default AgentBadge
